// The registry-driven WIDGETS dispatch table (ported discipline from
// rich-agent-gen renderers.tsx): one entry per message-type id renders that
// assistant part. Adding a type is one entry here. The SAME renderPart drives
// both the customer widget (App.tsx) and the operator Timeline
// (@retink/connector-webchat/renderers) — one renderer, no fork.
//
// `renderPart` retains its original string callback for external/operator users;
// `renderStructuredPart` gives the SDK widget the correlated result object.
import { useState, type ReactNode } from 'react'
import type { Part } from '@retink/webchat-sdk'

export type OnAnswer = (value: string) => void
export type StructuredOnAnswer = (result: unknown) => void

function FormWidget({ part, onAnswer, disabled }: { part: Part; onAnswer: StructuredOnAnswer; disabled: boolean }) {
  const fields = (part['fields'] as Array<{ name: string; label: string; type?: string; placeholder?: string; required?: boolean }>) ?? []
  const [values, setValues] = useState<Record<string, string>>({})
  return (
    <div className="rc-bubble assistant">
      {part['text'] ? <div className="rc-prompt">{String(part['text'])}</div> : null}
      <form
        className="rc-form"
        onSubmit={(e) => {
          e.preventDefault()
          onAnswer({ values })
        }}
      >
        {fields.map((f) => (
          <label key={f.name} className="rc-field">
            <span>{f.label}</span>
            {f.type === 'textarea' ? (
              <textarea
                placeholder={f.placeholder ?? ''}
                required={f.required}
                disabled={disabled}
                value={values[f.name] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              />
            ) : (
              <input
                type={f.type ?? 'text'}
                placeholder={f.placeholder ?? ''}
                required={f.required}
                disabled={disabled}
                value={values[f.name] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              />
            )}
          </label>
        ))}
        <button type="submit" disabled={disabled}>{String(part['submitLabel'] ?? 'Submit')}</button>
      </form>
    </div>
  )
}

function DateTimeWidget({ part, onAnswer, disabled }: { part: Part; onAnswer: StructuredOnAnswer; disabled: boolean }) {
  const mode = (part['mode'] as string) ?? 'datetime'
  const inputType = mode === 'date' ? 'date' : mode === 'time' ? 'time' : 'datetime-local'
  const [value, setValue] = useState('')
  return (
    <div className="rc-bubble assistant">
      {part['text'] ? <div className="rc-prompt">{String(part['text'])}</div> : null}
      <form
        className="rc-form"
        onSubmit={(e) => {
          e.preventDefault()
          if (value) onAnswer({ value })
        }}
      >
        <input
          type={inputType}
          value={value}
          disabled={disabled}
          onChange={(e) => setValue(e.target.value)}
          {...(part['min'] ? { min: String(part['min']) } : {})}
          {...(part['max'] ? { max: String(part['max']) } : {})}
        />
        <button type="submit" disabled={!value || disabled}>Confirm</button>
      </form>
    </div>
  )
}

function RatingWidget({ part, onAnswer, disabled }: { part: Part; onAnswer: StructuredOnAnswer; disabled: boolean }) {
  const max = Number(part['max'] ?? 5)
  const symbol = part['icon'] === 'heart' ? '♥' : '★'
  const [hover, setHover] = useState(0)
  const [picked, setPicked] = useState(0)
  const active = hover || picked
  return (
    <div className="rc-bubble assistant">
      {part['text'] ? <div className="rc-prompt">{String(part['text'])}</div> : null}
      <div className="rc-rating" role="radiogroup">
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            className={`rc-star${n <= active ? ' on' : ''}`}
            aria-label={`${n} of ${max}`}
            disabled={disabled}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => { setPicked(n); onAnswer({ value: n }) }}
          >
            {symbol}
          </button>
        ))}
      </div>
    </div>
  )
}

function SelectWidget({ part, onAnswer, disabled }: { part: Part; onAnswer: StructuredOnAnswer; disabled: boolean }) {
  const options = (part['options'] as Array<{ label: string; value: string }>) ?? []
  const [selectedIndex, setSelectedIndex] = useState(-1)
  return (
    <div className="rc-bubble assistant">
      {part['text'] ? <div className="rc-prompt">{String(part['text'])}</div> : null}
      <form
        className="rc-select-form"
        onSubmit={(event) => {
          event.preventDefault()
          const selected = options[selectedIndex]
          if (selected) onAnswer({ value: selected.value, label: selected.label })
        }}
      >
        <select value={selectedIndex} disabled={disabled} aria-label={String(part['text'] ?? 'Choose an option')} onChange={(event) => setSelectedIndex(Number(event.target.value))}>
          <option value={-1}>Choose an option…</option>
          {options.map((option, index) => <option key={`${option.value}:${index}`} value={index}>{option.label}</option>)}
        </select>
        <button type="submit" disabled={disabled || selectedIndex < 0}>Continue</button>
      </form>
    </div>
  )
}

const WIDGETS: Record<string, (part: Part, onAnswer: StructuredOnAnswer, disabled: boolean) => ReactNode> = {
  text: (part) => <div className="rc-bubble assistant">{String(part['text'] ?? '')}</div>,

  choice: (part, onAnswer, disabled) => {
    const options = (part['options'] as Array<{ label: string; value: string }>) ?? []
    if (part['presentation'] === 'select') return <SelectWidget part={part} onAnswer={onAnswer} disabled={disabled} />
    return (
      <div className="rc-bubble assistant">
        {part['text'] ? <div className="rc-prompt">{String(part['text'])}</div> : null}
        <div className="rc-choices">
          {options.map((o) => (
            <button key={o.value} type="button" disabled={disabled} onClick={() => onAnswer({ value: o.value, label: o.label })}>{o.label}</button>
          ))}
        </div>
      </div>
    )
  },

  form: (part, onAnswer, disabled) => <FormWidget part={part} onAnswer={onAnswer} disabled={disabled} />,

  card: (part, onAnswer, disabled) => {
    const actions = (part['actions'] as Array<{ label: string; value: string }>) ?? []
    return (
      <div className="rc-bubble assistant rc-card">
        {part['imageUrl'] ? <img className="rc-card-img" src={String(part['imageUrl'])} alt="" /> : null}
        <div className="rc-card-body">
          <div className="rc-card-title">{String(part['title'] ?? '')}</div>
          {part['subtitle'] ? <div className="rc-card-sub">{String(part['subtitle'])}</div> : null}
          {part['body'] ? <div className="rc-card-text">{String(part['body'])}</div> : null}
          {actions.length ? (
            <div className="rc-choices">
              {actions.map((a) => (
                <button key={a.value} type="button" disabled={disabled} onClick={() => onAnswer({ value: a.value, label: a.label })}>{a.label}</button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    )
  },

  carousel: (part) => {
    const cards = (part['cards'] as Array<{ title: string; subtitle?: string; imageUrl?: string }>) ?? []
    return (
      <div className="rc-carousel">
        {cards.map((card, i) => (
          <div key={i} className="rc-card rc-carousel-card">
            {card.imageUrl ? <img className="rc-card-img" src={card.imageUrl} alt="" /> : null}
            <div className="rc-card-body">
              <div className="rc-card-title">{card.title}</div>
              {card.subtitle ? <div className="rc-card-sub">{card.subtitle}</div> : null}
            </div>
          </div>
        ))}
      </div>
    )
  },

  image: (part) => (
    <div className="rc-bubble assistant rc-image">
      <img src={String(part['url'] ?? '')} alt={String(part['caption'] ?? '')} />
      {part['caption'] ? <div className="rc-card-sub">{String(part['caption'])}</div> : null}
    </div>
  ),

  rating: (part, onAnswer, disabled) => <RatingWidget part={part} onAnswer={onAnswer} disabled={disabled} />,

  datetime: (part, onAnswer, disabled) => <DateTimeWidget part={part} onAnswer={onAnswer} disabled={disabled} />,

  links: (part) => {
    const links = (part['links'] as Array<{ label: string; url: string }>) ?? []
    return (
      <div className="rc-bubble assistant">
        {part['text'] ? <div className="rc-prompt">{String(part['text'])}</div> : null}
        <div className="rc-choices">
          {links.map((l, i) => (
            <a key={i} className="rc-link" href={l.url} target="_blank" rel="noopener noreferrer">{l.label}</a>
          ))}
        </div>
      </div>
    )
  },

  file: (part) => (
    // SCALE: upload storage is not plumbed — this control is a disabled stub.
    <div className="rc-bubble assistant">
      {part['text'] ? <div className="rc-prompt">{String(part['text'])}</div> : null}
      <input type="file" disabled {...(part['accept'] ? { accept: String(part['accept']) } : {})} />
      <div className="rc-sys">File upload is coming soon.</div>
    </div>
  ),

  // Voice-channel parts (registered in message-types.ts): spoken on the call's
  // media plane, so here they render as their transcript text — the operator
  // Timeline (and any replayed widget transcript) shows what was said.
  say: (part) => <div className="rc-bubble assistant">{String(part['text'] ?? '')}</div>,
  voicemail: (part) => <div className="rc-bubble assistant">{String(part['prompt'] ?? '')}</div>,
}

export function renderStructuredPart(part: Part, onAnswer: StructuredOnAnswer, disabled = false): ReactNode {
  const widget = WIDGETS[part.type]
  if (widget) return widget(part, onAnswer, disabled)
  // An unregistered type renders LOUD, not blank: the old `?? WIDGETS['text']`
  // fallback collapsed an unknown part to an empty text bubble (silent data
  // loss). A visible marker makes a missing renderer diagnosable in both the
  // customer widget and the operator timeline.
  return <div className="rc-bubble assistant rc-sys">No renderer for “{String(part.type)}”</div>
}

export function renderPart(part: Part, onAnswer: OnAnswer): ReactNode {
  return renderStructuredPart(part, (result) => {
    const structured = result as { value?: unknown; values?: unknown }
    if (structured && typeof structured === 'object' && 'value' in structured) {
      onAnswer(String(structured.value ?? ''))
    } else if (structured && typeof structured === 'object' && 'values' in structured) {
      onAnswer(JSON.stringify(structured.values))
    } else {
      onAnswer(String(result ?? ''))
    }
  })
}
